/**
 * features/legal/legalContent.ts
 * ------------------------------------------------------------------
 * The words of the public legal pages — /privacy, /terms and
 * /data-deletion — kept apart from their layout (LegalPage.tsx) the same way
 * the company page keeps its copy in work.ts, so a wording change never
 * touches layout.
 *
 * WHY THESE EXIST. Any company that runs a public enquiry form and sends
 * messages on schools' behalf needs a privacy policy, terms and data-deletion
 * instructions at stable public URLs. (They were first written for Meta's
 * WhatsApp review; Acepsle no longer uses WhatsApp — see retired/whatsapp/.)
 *
 * KEEP THEM TRUE. Every statement here describes what the hub actually does
 * today — no analytics or advertising trackers, the enquiry form's five
 * fields, the one browser setting stored — plus the school messaging service
 * (text messages, email and app notifications) as designed. Name the SMS
 * delivery company in "Who we share it with" once one is contracted. If a feature changes what is collected, shared or kept, change
 * this file in the same commit. A privacy policy that has drifted from the
 * system is worse than none.
 *
 * DRAFTED, NOT LEGALLY REVIEWED. Written against the Botswana Data
 * Protection Act, 2018; have it read by a Botswana legal practitioner before
 * relying on it. The retention periods are Acepsle's operating defaults.
 * ---------------------------------------------------------------- */
import { COMPANY } from '@/features/landing/work';

export type LegalSlug = 'privacy' | 'terms' | 'data-deletion';

/** A section is a heading and a run of blocks: a string is a paragraph, a
 *  string[] is a bulleted list. */
export interface LegalSection {
  heading: string;
  body: Array<string | string[]>;
}

export interface LegalDoc {
  slug: LegalSlug;
  title: string;
  /** Shown as "Last updated" — change it whenever the substance changes. */
  updated: string;
  intro: string;
  sections: LegalSection[];
}

const WHO = `${COMPANY.legalName} (trading as ${COMPANY.displayName}), a company registered in Botswana under registration number ${COMPANY.registration}, of ${COMPANY.address}`;
const CONTACT = `${COMPANY.email} or ${COMPANY.phone}`;

export const LEGAL_DOCS: Record<LegalSlug, LegalDoc> = {
  /* ================================================================ */
  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    updated: '14 September 2026',
    intro: `This policy explains what personal information ${COMPANY.legalName} collects, why, who it is shared with, how long it is kept, and the choices you have. It is written with the Botswana Data Protection Act, 2018 in mind.`,
    sections: [
      {
        heading: 'Who we are',
        body: [
          `${WHO}. You can reach us at ${CONTACT}.`,
          'This policy covers this website, enquiries sent to us, the business services we provide to our clients, and the school messaging service described below. It does not cover the AcePSLE study app, which is provided separately.',
        ],
      },
      {
        heading: 'Two roles we play',
        body: [
          [
            'When you contact us or become our client, we decide how your information is used. We are responsible for it.',
            'When we build and run systems for a school — including sending messages to parents on a school’s behalf — the school is responsible for the personal information of its pupils, parents and staff. We process that information only to provide the service the school has asked for, on the school’s instructions, and for no purpose of our own.',
          ],
        ],
      },
      {
        heading: 'What we collect',
        body: [
          [
            'Enquiries: your name, email address, phone number, organisation and message, when you use the enquiry form on this site or write to us.',
            'Clients: contact and billing details needed to prepare quotes, invoices, receipts and statements.',
            'School messaging service: a parent or guardian’s name, mobile number and email address as recorded by the school, the messages the school sends (such as a notice that a teacher has left a message in the school portal, fee statements, report notices, absence notices and login details), and the delivery status reported for each message (for example sent, delivered or failed).',
            'Your browser: this site remembers only whether you chose a light or dark display, in your browser’s local storage. It does not use analytics, advertising or tracking cookies.',
          ],
        ],
      },
      {
        heading: 'Why we use it',
        body: [
          [
            'To reply to your enquiry and, if you ask, prepare a proposal.',
            'To provide the services we have agreed with our clients, and to bill for them.',
            'To send a school’s messages to parents and guardians who have agreed to receive them, and to show the school whether each message arrived.',
            'To keep our systems secure and working.',
            'To keep the financial records that Botswana tax and company law require.',
          ],
        ],
      },
      {
        heading: 'Messages sent for schools',
        body: [
          'A school’s messages reach parents and guardians as text messages (SMS), email, or notifications from the school portal on a device where the parent has switched them on. Text messages are delivered through a licensed SMS delivery company acting on our instructions.',
          'We send text messages only to numbers the school has recorded as having agreed to receive them. You can ask to stop receiving them at any time by telling the school, or by contacting us — we will stop sending to that number.',
          'We never use a school’s parent or guardian details to market anything, and we do not combine them with information from other schools or sources.',
        ],
      },
      {
        heading: 'Who we share it with',
        body: [
          'We do not sell personal information. We share it only with the service providers we need to operate:',
          [
            'Google, which hosts our systems and databases (Google Cloud and Firebase) and our email.',
            'An SMS delivery company, which delivers text messages to mobile networks.',
          ],
          'We may also disclose information where the law requires it.',
        ],
      },
      {
        heading: 'Where it is stored',
        body: [
          'Our systems run on Google Cloud servers located outside Botswana, currently in the United States, and text messages pass through our SMS delivery company’s systems. Where information leaves Botswana we rely on these providers’ contractual and security commitments to protect it.',
        ],
      },
      {
        heading: 'How long we keep it',
        body: [
          [
            'Enquiries that do not lead to work: up to 24 months after our last contact.',
            'Client and financial records: for as long as Botswana tax and company law requires.',
            'School messaging records (message log and delivery status): 12 months, unless the school instructs a shorter period, and deleted when our service to the school ends.',
          ],
        ],
      },
      {
        heading: 'How we protect it',
        body: [
          'Access is limited to authorised Acepsle staff who need it. Information is encrypted in transit, credentials for connected services are held server-side and never in web pages, and every school’s information is kept separate from every other school’s.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'You may ask to see the personal information we hold about you, to correct it, or to have it deleted, and you may object to how it is used. Write to us at the address above. For information we hold on a school’s behalf, you can also ask the school directly; we will help the school respond.',
          'If you are not satisfied with our response, you may complain to the Information and Data Protection Commission of Botswana.',
          'To have your information deleted, see our data deletion instructions at /data-deletion.',
        ],
      },
      {
        heading: 'Children',
        body: [
          'The school systems we build hold information about pupils on schools’ behalf and under their control. Our messaging service writes to parents and guardians, not to pupils.',
        ],
      },
      {
        heading: 'Changes to this policy',
        body: [
          'We will update this page when our practices change and revise the date at the top. Significant changes affecting a school’s information will be communicated to that school.',
        ],
      },
    ],
  },

  /* ================================================================ */
  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    updated: '14 September 2026',
    intro: `These terms apply to this website and to the services provided by ${COMPANY.legalName}. By using the website or our services you agree to them.`,
    sections: [
      {
        heading: 'Who we are',
        body: [`${WHO}. Contact: ${CONTACT}.`],
      },
      {
        heading: 'Our services',
        body: [
          'We design, build and support software, including school management systems, business and finance systems, and messaging on behalf of our clients. The scope, fees and timelines of any engagement are set out in a written quote, proposal or agreement. Where such a document conflicts with these terms, the document prevails.',
        ],
      },
      {
        heading: 'Using this website',
        body: [
          'You may use this website to learn about us and to contact us. You must not attempt to interfere with its operation, gain unauthorised access to any part of it or to our systems, or use it to send unlawful or unsolicited material.',
        ],
      },
      {
        heading: 'Messaging on behalf of schools',
        body: [
          'Where we send messages for a school:',
          [
            'The school is responsible for the content of its messages and for obtaining and recording the agreement of the parents and guardians it messages.',
            'Messages must be lawful and relate to the school’s pupils. We may decline or suspend sending that would breach the law or mobile network rules.',
            'Text messages are charged as agreed in writing with the school.',
            'Delivery depends on mobile networks, email providers and the recipient’s device. We report the delivery status we are given but cannot guarantee that any message will be delivered or read.',
          ],
        ],
      },
      {
        heading: 'Intellectual property',
        body: [
          'The content of this website and the software we create remain the property of Acepsle Proprietary Limited unless a written agreement transfers or licenses them. Our clients’ own data remains theirs.',
        ],
      },
      {
        heading: 'Availability and liability',
        body: [
          'We work to keep our website and services available and accurate, but they are provided without guarantee of uninterrupted or error-free operation. To the extent permitted by the laws of Botswana, we are not liable for indirect or consequential loss arising from use of this website. Our liability in relation to a paid service is limited as set out in the agreement for that service.',
        ],
      },
      {
        heading: 'Privacy',
        body: ['How we handle personal information is described in our privacy policy at /privacy.'],
      },
      {
        heading: 'Governing law',
        body: ['These terms are governed by the laws of Botswana, and the courts of Botswana have jurisdiction over any dispute arising from them.'],
      },
      {
        heading: 'Changes',
        body: ['We may update these terms and will revise the date at the top of this page when we do.'],
      },
    ],
  },

  /* ================================================================ */
  'data-deletion': {
    slug: 'data-deletion',
    title: 'Data Deletion Instructions',
    updated: '14 September 2026',
    intro: `How to ask ${COMPANY.legalName} to delete personal information we hold about you, including records of messages sent to you on a school’s behalf.`,
    sections: [
      {
        heading: 'How to make a request',
        body: [
          `Email ${COMPANY.email} with the subject line "Data deletion request". Please include:`,
          [
            'your full name;',
            'the email address or mobile number the information relates to;',
            'if it concerns messages from a school, the name of the school.',
          ],
          `You can also call us on ${COMPANY.phone}.`,
        ],
      },
      {
        heading: 'What happens next',
        body: [
          [
            'We confirm we have received your request within 5 working days, and may ask you to confirm your identity so that we do not delete someone else’s information by mistake.',
            'Where the information is held on a school’s behalf, we tell the school, which is responsible for it, and act on the request together with them.',
            'We delete the information within 30 days of confirming the request, and write to tell you when it is done.',
          ],
        ],
      },
      {
        heading: 'What is deleted',
        body: [
          [
            'Your enquiry and contact details.',
            'For school messaging: your mobile number in our messaging records, the log of messages sent to you, and their delivery status. We will also stop sending text messages to your number.',
          ],
        ],
      },
      {
        heading: 'What may be kept',
        body: [
          'We may keep information that the law requires us to retain, such as financial records needed for tax purposes, for as long as the law requires, and will tell you if so.',
          'Messages already delivered to your phone or inbox are under your own control — you can delete them there.',
        ],
      },
    ],
  },
};
